import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { fetchPublicOperator } from "../lib/api";
import { AvatarChip } from "./AvatarChip";
import { TierBadge } from "./TierBadge";
import { PlatformChips } from "./PlatformChips";
import { LiveBadge } from "./LiveBadge";

/**
 * PublicOperatorPage — share-friendly, no-auth profile at
 * `/u/{handle}`. Renders a simplified holo card with the operator's
 * competitive identity + a "Create your operator" CTA for signed-out
 * visitors.
 *
 * The handle resolves server-side (`GET /api/public/u/{handle}`) to
 * either the canonical username, the legacy referral_code, or a slug
 * match on the display name.
 *
 * Privacy:
 *   The backend strips email/user_id/payment/admin/inbox fields. Only
 *   user-controlled public bits land in `op` here.
 */
export default function PublicOperatorPage({ onToast, user }) {
  const { handle } = useParams();
  const navigate = useNavigate();
  const [op, setOp] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setErr("");
    fetchPublicOperator(handle)
      .then((d) => { if (!cancel) { setOp(d); setLoading(false); } })
      .catch((e) => {
        if (cancel) return;
        const status = e?.response?.status;
        setErr(status === 404 ? "Operator not found" : (e?.response?.data?.detail || "Something went wrong"));
        setLoading(false);
      });
    return () => { cancel = true; };
  }, [handle]);

  // Update <head> tags on this route so direct-loads (e.g. someone
  // opening the link after auth) get a nicer title. NB: this doesn't
  // help bot crawlers — they need SSR — but it's a free win for users.
  useEffect(() => {
    if (!op) return;
    const title = `${op.name} · LEAGUE HUB`;
    const desc = op.bio?.trim() || `${op.name} — operator on LEAGUE HUB · MMR ${op.mmr}`;
    document.title = title;
    setMeta("og:title", title);
    setMeta("og:description", desc);
    setMeta("description", desc);
    setMeta("og:type", "profile");
    setMeta("og:url", window.location.href);
    setMeta("twitter:card", "summary");
    setMeta("twitter:title", title);
    setMeta("twitter:description", desc);
  }, [op]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="public-operator-loading">
        <div className="h-8 w-8 rounded-full border-2 border-[#14b8a6]/40 border-t-[#14b8a6] animate-spin" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 py-16 text-center" data-testid="public-operator-404">
        <p className="text-[11px] tracking-[3px] text-[#14b8a6] mb-2">404 · NO SIGNAL</p>
        <h1 className="font-display grad-text text-4xl sm:text-5xl mb-4">OPERATOR NOT FOUND</h1>
        <p className="text-white/60 max-w-md mb-8">
          We couldn&apos;t find <span className="text-[#14b8a6] font-mono">{handle}</span> on the grid.
          Double-check the link or browse active operators below.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link
            to="/players"
            data-testid="public-operator-browse-cta"
            className="text-[11px] tracking-widest px-5 py-3 rounded-xl bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25"
          >
            <i className="fas fa-users mr-1.5" />BROWSE OPERATORS
          </Link>
          {!user && (
            <Link
              to="/"
              data-testid="public-operator-signup-cta"
              className="text-[11px] tracking-widest px-5 py-3 rounded-xl bg-white text-black hover:bg-white/90"
            >
              <i className="fas fa-bolt mr-1.5" />CREATE YOUR OPERATOR — FREE
            </Link>
          )}
        </div>
      </div>
    );
  }

  const shareUrl = `${window.location.origin}/u/${op.username}`;
  const isPro = op.user_type === "pro" || op.user_type === "elite";

  const onShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${op.name} on LEAGUE HUB`,
          text: op.bio?.trim() || `${op.name} · MMR ${op.mmr}`,
          url: shareUrl,
        });
        return;
      } catch {
        // user cancelled or share unsupported — fall through to copy
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      onToast?.("📋 Link copied — paste it anywhere");
    } catch {
      onToast?.(shareUrl);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="public-operator-page">
      {/* Hero banner — uses the user's PRO banner when set, otherwise a
          subtle teal gradient that matches the in-app holo card. */}
      <div
        className="rounded-3xl h-32 sm:h-44 mb-[-44px] sm:mb-[-56px] relative overflow-hidden border border-white/10"
        style={op.banner_url ? {
          backgroundImage: `url(${op.banner_url})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        } : {
          background: "linear-gradient(135deg, #001a26 0%, #14b8a644 100%)",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/0" />
      </div>

      <div className="glass-strong relative rounded-3xl p-6 sm:p-7 border-2 border-[#14b8a6]/30">
        <div className="flex items-start gap-4">
          <div className="shrink-0 -mt-12 sm:-mt-16 ring-4 ring-black rounded-3xl">
            <AvatarChip avatarId={op.avatar_id} pfpUrl={op.pfp_url} size={88} />
          </div>
          <div className="min-w-0 flex-1 pt-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1
                data-testid="public-operator-name"
                className="font-display text-2xl sm:text-3xl truncate"
              >
                {op.name}
              </h1>
              <TierBadge plan={isPro ? "pro" : "casual"} />
              {op.live && <LiveBadge live={op.live} dataTestid="public-operator-live-badge" />}
            </div>
            <p className="text-[11px] tracking-widest text-white/40 mt-1">
              <span className="text-[#14b8a6] font-mono">leaguehub.co/u/{op.username}</span>
              {op.joined_year && <span className="ml-3">· OPERATOR SINCE {op.joined_year}</span>}
            </p>
            {op.primary_game && (
              <p className="text-sm text-white/70 mt-2">
                <i className="fas fa-gamepad mr-1 text-[#14b8a6]" /> Primary: <strong>{op.primary_game}</strong>
              </p>
            )}
          </div>
        </div>

        {/* Stat row — MMR · top games · teams · badges */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          <Stat label="MMR" value={op.mmr.toLocaleString()} testid="public-operator-mmr" />
          <Stat label="GAMES" value={(op.games_played?.length || 0)} testid="public-operator-games-count" />
          <Stat label="TEAMS" value={op.teams_count || 0} testid="public-operator-teams-count" />
          <Stat label="WINS" value={op.verified_tournament_wins || 0} testid="public-operator-wins-count" suffix="🏆" />
        </div>

        {op.bio?.trim() && (
          <p data-testid="public-operator-bio" className="mt-5 text-white/85 leading-relaxed">
            {op.bio}
          </p>
        )}

        {op.top_games?.length > 0 && (
          <div className="mt-5">
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] mb-2">
              <i className="fas fa-gamepad mr-1" />TOP GAMES
            </p>
            <div className="flex flex-wrap gap-1.5">
              {op.top_games.slice(0, 3).map((g, idx) => (
                <span
                  key={g.game}
                  className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md bg-[#14b8a6]/10 border border-[#14b8a6]/30 text-[#14b8a6]"
                >
                  <span className="font-bold">#{idx + 1}</span>
                  <span>{g.game}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Connected gaming platforms — reuses the same component as the holo card */}
        <div className="mt-5">
          <p className="text-[10px] tracking-[3px] text-[#14b8a6] mb-2">
            <i className="fas fa-link mr-1" />CONNECTED
          </p>
          <PlatformChips
            source={op}
            testIdPrefix="public-operator-platforms"
            onCopy={onToast}
          />
        </div>

        {/* Social — Discord / X / Twitch / YouTube / Kick / Website */}
        {(op.discord_handle || op.x_handle || op.twitch_handle || op.youtube_url || op.kick_url || op.website_url) && (
          <div className="mt-5">
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] mb-2">
              <i className="fas fa-share-nodes mr-1" />SOCIAL
            </p>
            <div className="flex flex-wrap gap-1.5">
              {op.discord_handle && (
                <span className="text-[11px] px-2.5 py-1 rounded-full bg-black/40 border border-white/10 text-white/80">
                  <i className="fab fa-discord mr-1" /> {op.discord_handle}
                </span>
              )}
              {op.x_handle && (
                <a
                  href={`https://x.com/${encodeURIComponent(op.x_handle)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-[11px] px-2.5 py-1 rounded-full bg-black/40 border border-white/10 text-white/80 hover:border-[#14b8a6]/60 hover:text-[#14b8a6]"
                >
                  <i className="fab fa-x-twitter mr-1" /> @{op.x_handle}
                </a>
              )}
              {op.twitch_handle && (
                <a
                  href={`https://twitch.tv/${encodeURIComponent(op.twitch_handle)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-[11px] px-2.5 py-1 rounded-full bg-black/40 border border-white/10 text-white/80 hover:border-[#14b8a6]/60 hover:text-[#14b8a6]"
                >
                  <i className="fab fa-twitch mr-1" /> {op.twitch_handle}
                </a>
              )}
              {op.youtube_url && (
                <a
                  href={op.youtube_url.startsWith("http") ? op.youtube_url : `https://youtube.com/@${op.youtube_url}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-[11px] px-2.5 py-1 rounded-full bg-black/40 border border-white/10 text-white/80 hover:border-[#14b8a6]/60 hover:text-[#14b8a6]"
                >
                  <i className="fab fa-youtube mr-1" /> YouTube
                </a>
              )}
              {op.kick_url && (
                <a
                  href={op.kick_url.startsWith("http") ? op.kick_url : `https://kick.com/${op.kick_url}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="public-operator-kick-link"
                  className="text-[11px] px-2.5 py-1 rounded-full bg-black/40 border border-[#53FC18]/30 text-[#53FC18]/90 hover:bg-[#53FC18]/10"
                >
                  <i className="fas fa-tower-broadcast mr-1" /> Kick
                </a>
              )}
              {op.website_url && (
                <a
                  href={op.website_url.startsWith("http") ? op.website_url : `https://${op.website_url}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-[11px] px-2.5 py-1 rounded-full bg-black/40 border border-white/10 text-white/80 hover:border-[#14b8a6]/60 hover:text-[#14b8a6]"
                >
                  <i className="fas fa-globe mr-1" /> Website
                </a>
              )}
            </div>
          </div>
        )}

        {/* Action bar — Share + signed-out CTA */}
        <div className="mt-7 pt-5 border-t border-white/10 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={onShare}
            data-testid="public-operator-share-btn"
            className="text-[11px] tracking-widest px-4 py-2.5 rounded-xl bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25"
          >
            <i className="fas fa-share-nodes mr-1.5" />SHARE PROFILE
          </button>
          {!user && (
            <Link
              to={op.referral_code ? `/r/${op.referral_code}` : "/"}
              data-testid="public-operator-cta-signup"
              className="text-[11px] tracking-widest px-4 py-2.5 rounded-xl bg-white text-black hover:bg-white/90"
            >
              <i className="fas fa-bolt mr-1.5" />CREATE YOUR OPERATOR — FREE
            </Link>
          )}
          {user && user.username !== op.username && (
            <button
              type="button"
              onClick={() => navigate("/players")}
              className="text-[11px] tracking-widest px-4 py-2.5 rounded-xl bg-white/5 text-white/80 border border-white/10 hover:bg-white/10"
            >
              <i className="fas fa-users mr-1.5" />BROWSE OPERATORS
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, testid, suffix }) {
  return (
    <div className="bg-black/40 border border-white/10 rounded-2xl px-3 py-2.5 text-center">
      <p className="text-[10px] tracking-[2px] text-white/40">{label}</p>
      <p data-testid={testid} className="text-xl font-display grad-text mt-0.5">
        {value}{suffix && <span className="ml-1 text-sm">{suffix}</span>}
      </p>
    </div>
  );
}

/** Idempotent <meta name|property=...> upsert helper. */
function setMeta(key, content) {
  if (!content) return;
  const attr = key.startsWith("og:") || key.startsWith("twitter:")
    ? "property" : "name";
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}
